"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-foreground text-[12px] uppercase tracking-[0.2em] font-mono bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-2 group">
                <span>{item.label}</span>
                <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 mt-12 px-2">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-default text-3xl text-foreground tracking-tight opacity-0 animate-[fadeIn_600ms_ease-out_forwards] hover:text-primary transition-colors" style={{
        animationDelay: `${i * 80 + 100}ms`
      }}>
            {item.label}
          </Link>
        </SheetClose>)}
      <SheetClose asChild>
        <Link to="/QualificationApplicationPage" className="mt-6 inline-flex w-fit items-center justify-center rounded-md border border-primary px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] font-mono text-primary hover:bg-primary hover:text-secondary transition-colors duration-300 opacity-0 animate-[fadeIn_600ms_ease-out_forwards]" style={{
        animationDelay: `${menuItems.length * 80 + 200}ms`
      }}>
          Start the Qualification Application
        </Link>
      </SheetClose>
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3682" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${scrolled ? "backdrop-blur-lg border-b border-border/40 shadow-md" : "border-b border-transparent"}`}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="relative mx-auto w-full px-6 md:px-10 lg:px-16 py-5 md:py-7">
        <div className="flex items-center justify-between lg:grid lg:grid-cols-3">
          {}
          <div className="hidden lg:flex items-center justify-start">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex lg:justify-center">
            <Link to="/" className="inline-flex items-center" aria-label="Benewealth Financial home">
              <WvcLogo className="h-7 w-auto text-foreground" />
            </Link>
          </div>

          {}
          <div className="flex items-center justify-end gap-2">
            <div className="hidden lg:block">
              <Button asChild variant="outline" className="rounded-md border border-primary bg-transparent text-primary px-5 py-2 h-auto text-[12px] uppercase tracking-[0.2em] font-mono hover:bg-primary hover:text-secondary transition-colors duration-400">
                <Link to="/QualificationApplicationPage">Start the Qualification Application</Link>
              </Button>
            </div>

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent">
                    <Menu className="size-6" strokeWidth={1} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background w-full sm:max-w-md border-l border-border">
                  <SheetHeader className="px-6 pt-6">
                    <SheetTitle className="font-default text-xl text-foreground text-left">
                      <WvcLogo className="h-7 w-auto text-foreground" />
                    </SheetTitle>
                  </SheetHeader>
                  <div className="px-6 pb-10 overflow-y-auto">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}